
/**
 * Format T-shirt size for display
 * @param size The raw t-shirt size from the database
 * @returns Formatted t-shirt size for display
 */
export const formatTShirtSize = (size?: string): string => {
  if (!size) return "N/A";
  
  // Basic formatting for common size formats
  if (size.includes('Nu_')) {
    return size.replace('Nu_', '') + ' (Nữ)';
  } else if (size.includes('Nam_')) {
    return size.replace('Nam_', '') + ' (Nam)';
  } else if (size.includes('_Nu')) {
    return size.replace('_Nu', '') + ' (Nữ)';
  } else if (size.includes('_Nam')) {
    return size.replace('_Nam', '') + ' (Nam)';
  }
  
  // Handle other formats 
  if (size.includes('(Nam)') || size.includes('(Nữ)')) {
    return size; // Already formatted
  }
  
  // For sizes like "XL" without gender specification
  return size;
};

/**
 * Format date/time for display
 * @param dateString ISO date string
 * @returns Formatted date/time for display
 */
export const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format time ago for display
 * @param dateString ISO date string
 * @returns Relative time (e.g., "2 hours ago")
 */
export const formatTimeAgo = (dateString?: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    
    // Convert to appropriate time unit
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) {
      return 'vài giây trước';
    } else if (diffMin < 60) {
      return `${diffMin} phút trước`;
    } else if (diffHour < 24) {
      return `${diffHour} giờ trước`;
    } else if (diffDay < 30) {
      return `${diffDay} ngày trước`;
    } else {
      return formatDateTime(dateString).split(',')[0]; // Just return the date part
    }
  } catch (error) {
    console.error('Error formatting time ago:', error);
    return dateString;
  }
};

/**
 * Format contribution amount for display
 * @param amount Contribution amount in VND
 * @returns Formatted amount (e.g., "200,000 ₫")
 */
export const formatContribution = (amount?: number): string => {
  if (!amount) return '0 ₫';
  
  try {
    return amount.toLocaleString('vi-VN') + ' ₫';
  } catch (error) {
    console.error('Error formatting contribution:', error);
    return amount + ' ₫';
  }
};

/**
 * Format currency for display
 * @param amount Amount in VND
 * @returns Formatted currency (e.g., "200,000 VNĐ")
 */
export const formatCurrency = (amount: number): string => {
  try {
    return `${amount.toLocaleString('vi-VN')} VNĐ`;
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${amount} VNĐ`;
  }
};
